import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';

export interface ModalState {
  open: boolean;
  id?: string;
  reason?: 'Create' | 'Update' | 'Duplicate';
  defaults?: Record<string, unknown>;
  hideParent?: boolean;
}

export function useModalState(name: string) {
  const [searchParams, setSearchParams] = useSearchParams();

  const modalState = useMemo<ModalState>(() => {
    const raw = searchParams.get(name);
    if (!raw) return { open: false };
    try { return JSON.parse(raw); } catch { return { open: false }; }
  }, [searchParams, name]);

  const setModal = useCallback((state: Partial<ModalState> | undefined) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (!state || !state.open) {
        next.delete(name);
      } else {
        next.set(name, JSON.stringify({ open: true, ...state }));
      }
      return next;
    }, { replace: true });
  }, [name, setSearchParams]);

  const toggleModal = useCallback((open: boolean) => {
    if (!open) setModal(undefined);
    else setModal({ open: true });
  }, [setModal]);

  return { modalState, setModal, toggleModal };
}
